import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Shape API de un slot del checklist / documento de sucursal. */
export interface BranchDocumentDto {
  id?: string | null;
  branchId?: string;
  countryDocumentId?: string;
  typeDocumentId?: string;
  typeDocumentPrefix?: string;
  typeDocumentCode?: string;
  typeDocumentName?: string;
  name: string;
  required?: boolean;
  fileUrl?: string | null;
  originalName?: string | null;
  contentType?: string;
  fileSize?: number;
  validityDate?: string | null;
  noExpiration?: boolean;
  status?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type BranchDocumentWritePayload = {
  countryDocumentId: string;
  validityDate?: string | null;
  noExpiration?: boolean;
};

export type BranchDocumentListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Documentos de sucursal (checklist por documentos del país).
 *   GET    /api/structure/branches/{branchId}/documents
 *   POST   /api/structure/branches/{branchId}/documents/upsert
 *   PUT    /api/structure/branches/{branchId}/documents/{id}
 *   PATCH  /api/structure/branches/{branchId}/documents/{id}/status
 *   DELETE /api/structure/branches/{branchId}/documents/{id}
 */
@Injectable({ providedIn: 'root' })
export class BranchDocumentService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(branchId: string): string {
    return `${this.apiConfig.apiUrl}/structure/branches/${branchId}/documents`;
  }

  list(
    branchId: string,
    params: BranchDocumentListParams = {},
  ): Observable<BranchDocumentDto[]> {
    let httpParams = new HttpParams();
    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<BranchDocumentDto[] | PageableDto<BranchDocumentDto>>
      >(this.resource(branchId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  upsert(
    branchId: string,
    payload: BranchDocumentWritePayload,
    file?: File | null,
  ): Observable<{ message: string; data?: BranchDocumentDto }> {
    return this.http
      .post<{
        success?: boolean;
        message?: string;
        data?: BranchDocumentDto;
      }>(`${this.resource(branchId)}/upsert`, this.toFormData(payload, file))
      .pipe(
        map((response) => ({
          ...mapMutationMessage(response, 'Documento guardado'),
          data: response.data,
        })),
      );
  }

  update(
    branchId: string,
    documentId: string,
    payload: Partial<BranchDocumentWritePayload>,
    file?: File | null,
  ): Observable<{ message: string }> {
    return this.http
      .put<{ success?: boolean; message?: string }>(
        `${this.resource(branchId)}/${documentId}`,
        this.toFormData(
          {
            countryDocumentId: payload.countryDocumentId ?? '',
            validityDate: payload.validityDate,
            noExpiration: payload.noExpiration,
          },
          file,
        ),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento actualizado'),
        ),
      );
  }

  updateStatus(
    branchId: string,
    documentId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(branchId)}/${documentId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    branchId: string,
    documentId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(branchId)}/${documentId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento eliminado exitosamente'),
        ),
      );
  }

  private toFormData(
    payload: BranchDocumentWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    if (payload.countryDocumentId) {
      formData.append('countryDocumentId', payload.countryDocumentId);
    }
    if (payload.noExpiration != null) {
      formData.append('noExpiration', String(payload.noExpiration));
    }
    if (payload.validityDate) {
      formData.append('validityDate', payload.validityDate);
    }
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | BranchDocumentDto[]
      | PageableDto<BranchDocumentDto>
      | null
      | undefined,
  ): BranchDocumentDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
